import React, { useState } from 'react';
import FileUploader from './components/FileUploader';
import ExcelEditor from './components/ExcelEditor';
import JsonDownloader from './components/JsonDownloader';
import './index.css';

const App: React.FC = () => {
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [jsonData, setJsonData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const API_URL = process.env.REACT_APP_API_URL;

  /** Upload PDF/Image -> Backend generates Excel */
  const handleFilesUpload = async (files: File[]) => {
    setLoading(true);
    setError(null);
    setJsonData(null);
    setExcelFile(null);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const response = await fetch(`${API_URL}/api/get_xlxs`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ERROR] Backend response:', errorText);
        throw new Error('Failed to convert file(s) to Excel');
      }
      const blob = await response.blob();
      const excel = new File([blob], 'menu.xlsx', { type: blob.type });
      setExcelFile(excel);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  /** Upload Excel directly for editing */
  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setExcelFile(file);
    setJsonData(null);
    setError(null);
  };

  /** Confirm Excel -> Backend converts to JSON */
  const handleExcelConfirm = async (updatedFile: File) => {
    setLoading(true);
    setError(null);
    setJsonData(null);

    try {
      const formData = new FormData();
      formData.append('file', updatedFile);
      const response = await fetch(`${API_URL}/api/get_json`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to convert Excel to JSON');
      const json = await response.json();
      setJsonData(json);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  /** Reset app state */
  const handleReset = () => {
    setExcelFile(null);
    setJsonData(null);
    setError(null);
    setLoading(false);
  };

  return (
    <div className="min-h-screen w-full bg-[#0f172a] text-white flex flex-col items-center py-10 px-4 transition-colors">
      <div className="w-full max-w-4xl rounded-3xl p-8 flex flex-col items-center shadow-[0_8px_24px_rgba(0,0,0,0.4)] bg-[#1e293b] border border-[#334155] box-border mx-auto">
        <h1 className="text-3xl font-extrabold mb-8 text-[#cbd5e1] tracking-tight text-center">
          Menu Excel Generator & Editor
        </h1>

        {error && (
          <div className="text-red-400 bg-[#2f1e1e] border border-red-600 rounded-xl px-4 py-2 mb-4 w-full text-center">
            {error}
          </div>
        )}

        {loading && (
          <div className="mb-4 text-sky-300 font-semibold text-center animate-pulse">
            🚀 Processing your file...
          </div>
        )}

        <div className="w-full">
          {!excelFile && !jsonData && (
            <div className="space-y-6">
              {/* Upload PDF/Image to convert */}
              <div className="rounded-2xl p-6 bg-[#1e293b] border border-[#334155] shadow-inner">
                <FileUploader onFilesUpload={handleFilesUpload} loading={loading}/>
              </div>

              {/* Upload Excel directly */}
              <div className="rounded-2xl p-6 bg-[#1e293b] border border-[#334155] text-center">
                <h2 className="mb-4 text-lg font-medium text-[#d0e4ff]">Already have an Excel file?</h2>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelUpload}
                  className="block mx-auto text-sm text-[#d0e4ff]
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-lg file:border-0
                            file:text-sm file:font-medium
                            file:bg-[#37597e] file:text-[#d0e4ff]
                            hover:file:bg-[#2f4d6d] cursor-pointer"
                />
              </div>
            </div>
          )}

          {excelFile && !jsonData && (
            <div className="rounded-2xl p-6 mb-4 bg-[#1e293b] border border-[#334155] shadow-inner">
              <ExcelEditor file={excelFile} onConfirm={handleExcelConfirm} />
            </div>
          )}

          {jsonData && (
            <div className="rounded-2xl p-6 mb-4 bg-[#1e293b] border border-[#334155] shadow-inner">
              <JsonDownloader jsonData={jsonData} onReset={handleReset} />
            </div>
          )}
        </div>
      </div>

      <footer className="mt-8 text-xs text-slate-400 text-center w-full max-w-4xl mx-auto px-2">
        &copy; {new Date().getFullYear()} <span className="text-sky-400">Menu Excel Generator</span>. All rights reserved.
      </footer>
    </div>
  );
};

export default App;
